// Geodesic measurement, in one place.
//
// The map spans a theatre, so planar shortcuts lie: a degree of longitude is
// 111 km at the equator and 96 km at Tehran's latitude, and a "square" drawn
// on the map is not a square on the ground. Distance is great-circle;
// AREA IS SPHERICAL EXCESS, not the shoelace formula on lon/lat — over a
// province-sized polygon at 30°N the shoelace answer is wrong by the cosine
// of the latitude, roughly 13%.
//
// The sphere (not the ellipsoid) is the right model here: it is within ~0.5%
// of WGS84 everywhere, which is far inside the error of a hand-drawn outline,
// and it needs no projection library — the frontend deliberately carries no
// geometry dependency (see raster.ts and gif.ts for the same reasoning).

export type LngLat = [number, number];

/** Mean Earth radius (IUGG), the same constant radar.ts measures with. */
export const EARTH_RADIUS_M = 6_371_008.8;

const RAD = Math.PI / 180;

/** Great-circle distance between two [lon, lat] points, in metres. */
export function haversineM(a: LngLat, b: LngLat): number {
  const dLat = (b[1] - a[1]) * RAD;
  const dLng = (b[0] - a[0]) * RAD;
  const la1 = a[1] * RAD;
  const la2 = b[1] * RAD;
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Length of an open path, in metres. Fewer than two points measures nothing. */
export function pathLengthM(pts: readonly LngLat[]): number {
  let d = 0;
  for (let i = 1; i < pts.length; i++) d += haversineM(pts[i - 1]!, pts[i]!);
  return d;
}

/** Perimeter of a ring, in metres: the path plus the closing edge. The ring
 *  may be given open or closed — a repeated last point adds a zero-length
 *  edge either way. */
export function ringPerimeterM(ring: readonly LngLat[]): number {
  if (ring.length < 3) return pathLengthM(ring);
  return pathLengthM(ring) + haversineM(ring[ring.length - 1]!, ring[0]!);
}

/** Area of a ring on the sphere, in square metres.
 *
 *  Spherical excess in the Chamberlain & Duquette form: each edge contributes
 *  the longitude it sweeps times the sine of the latitude it sweeps it at, and
 *  the sum closes to the solid angle the ring subtends. Winding order only
 *  flips the sign, so the result is absolute; a ring with fewer than three
 *  distinct corners has no area. Open or closed input both work — a repeated
 *  point contributes nothing. */
export function polygonAreaM2(ring: readonly LngLat[]): number {
  const n = ring.length;
  if (n < 3) return 0;
  let total = 0;
  for (let i = 0; i < n; i++) {
    const lo = ring[i]!;
    const mid = ring[(i + 1) % n]!;
    const hi = ring[(i + 2) % n]!;
    total += (hi[0] - lo[0]) * RAD * Math.sin(mid[1] * RAD);
  }
  return Math.abs((total * EARTH_RADIUS_M * EARTH_RADIUS_M) / 2);
}

/** The angle of a straight line ON THE MAP, clockwise from north in degrees.
 *
 *  Deliberately the Mercator (rhumb) angle, not the great-circle bearing:
 *  MapLibre draws a two-point LineString as a straight segment in projected
 *  space, so this is the angle that segment actually has on screen, and it is
 *  what an arrowhead placed at its end has to be rotated by. The two differ by
 *  degrees over a long line away from the equator — enough to leave a head
 *  visibly skewed off its own shaft. */
export function mercatorBearingDeg(a: LngLat, b: LngLat): number {
  // Mercator's y is the latitude stretched by the same factor the projection
  // stretches east–west, which is what makes the angle come out right.
  const merc = (lat: number): number =>
    Math.log(Math.tan(Math.PI / 4 + (Math.max(-85.05, Math.min(85.05, lat)) * RAD) / 2));
  let dLon = b[0] - a[0];
  if (dLon > 180) dLon -= 360;          // the short way round the antimeridian
  if (dLon < -180) dLon += 360;
  const dy = merc(b[1]) - merc(a[1]);
  return ((Math.atan2(dLon * RAD, dy) * 180) / Math.PI + 360) % 360;
}

/** Metres as m or km, the reading a measurement wants: two decimals under
 *  10 km, one under 100, whole numbers above. */
export function fmtDistance(m: number): string {
  if (m < 1000) return `${Math.round(m)} m`;
  const km = m / 1000;
  return `${km.toFixed(km < 10 ? 2 : km < 100 ? 1 : 0)} km`;
}

/** Square kilometres, the unit every area in this app is reported in
 *  (territory reshapes, split pieces, aircraft zones). */
export function fmtKm2(v: number): string {
  return `${v >= 100 ? Math.round(v).toLocaleString() : v.toFixed(1)} km²`;
}

/** Square metres in whatever unit reads: m² for a building, hectares for a
 *  compound, km² for anything a control map cares about. */
export function fmtArea(m2: number): string {
  if (m2 < 10_000) return `${Math.round(m2).toLocaleString()} m²`;
  const km2 = m2 / 1_000_000;
  if (km2 < 1) return `${(m2 / 10_000).toFixed(2)} ha`;
  if (km2 < 100) return `${km2.toFixed(km2 < 10 ? 2 : 1)} km²`;
  return fmtKm2(km2);
}
